import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { OptionsService } from 'projects/box-lib/src/lib/services/options.service';
import { AuthService } from '../../services/auth-service.service';

import {
  Consultant,
  EnvoiEmail,
  EnvoiEmailStatut,
  FileMetadata,
  Operation,
  OperationHistoryRecord,
  OperationStateTransitionTrigger,
  OperationTag,
  OperationType,
  TransactionPersonnelleEnum,
} from '../../models/generated/graphql';
import { OptionLabelPipe } from '../../pipes/optionLabel';
import { HabilitationCategories, checkConsultantHabilitation, filterHabilitations } from '../../utils/habilitation';

import { OperationHistoriqueDialogComponent } from './operation-historique-dialog/operation-historique-dialog.component';

import { OperationsDispatcherComponent } from '../operations-dashboard/operations-dispatcher/operations-dispatcher.component';
import { FicheLiaisonDialogComponent } from './fiche-liaison-dialog/fiche-liaison-dialog.component';
import {
  OperationChamp,
  OperationUpdateDialogComponent,
  OperationUpdateDialogData,
} from './operation-update-dialog/operation-update-dialog.component';

import { TemplatePortal } from '@angular/cdk/portal';
import { DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiBanner } from 'omnium-ui/banner';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { MenuNode } from 'omnium-ui/shared';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTagStatus } from 'omnium-ui/tag';
import { NotifyMailPartenaireComponent } from 'projects/box-lib/src/lib/components/notify-mail-partenaire/notify-mail-partenaire.component';
import { CommandeAramisDialogComponent } from 'projects/box-lib/src/lib/components/operations-panel-info/commande-aramis-dialog/commande-aramis-dialog.component';
import { isOperationBackofficeEditable } from 'projects/box-lib/src/lib/models/box-additionnal-data';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import { OperationSpecificData } from '../../models/donees-specifiques.model';
import { ApplicationInsightsService } from '../../services/ApplicationInsightService';
import { OperationsService } from '../../services/operations.service';
import { BoxDocumentViewerService } from '../document-viewer/document-viewer-panel/document-viewer-panel.service';
import { getTagAppearance, TagAppearance } from '../../utils/tag-appearance';
import { gql } from 'apollo-angular';
import { QueryManagerService } from '../../services/queryManagerService';

const MARK_CONSULTANT_COMMENT_AS_READ = gql`
  mutation markConsultantCommentAsRead($operationId: Int!) {
    markConsultantCommentAsRead(operationId: $operationId) {
      id
      commentaireConsultantLu
    }
  }
`;

interface ConsultantInfo {
  displayName: string;
  email: string | null;
  mailToLink: string | null;
  phoneNumber: string | null;
  commisionnement: string;
  titre: string;
}

@Component({
  selector: 'app-operations-panel-info',
  templateUrl: './operations-panel-info.component.html',
  styleUrls: ['./operations-panel-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OperationsPanelInfoComponent {
  private static readonly DRAFT_STATUT_IDS = [1, 5];
  private static readonly SCPI_RENDEMENT_SOUSCRIPTION_CONFIG_ID = 181;
  private static readonly ELIGIBLE_HABILITATIONS = ['ALPSI', 'CIF'];

  readonly OPERATION_MAIL_RECEPTION_TRIGGER = OperationStateTransitionTrigger.BackOfficeReceivesMailDocuments;

  @Input() isConsultant: boolean = false;
  @Input() operation: Operation;

  @Output() onRejectClick = new EventEmitter();
  @Output() onCancelClick = new EventEmitter();
  @Output() onRestoreClick = new EventEmitter();
  @Output() onEditGestionnaireCommentClick = new EventEmitter();
  @Output() onSetInstancePartenaireClick = new EventEmitter();
  @Output() operationChange = new EventEmitter();
  @Output() onMailReceptionClick = new EventEmitter();
  @Output() onGestionnaireAssigned = new EventEmitter<Operation>();
  @Output() onBackArrowClicked = new EventEmitter();
  @Output() onSendConsultantEmailClick = new EventEmitter();
  specificData: OperationSpecificData;
  allFilesChecked: boolean;
  isMarkingConsultantCommentAsRead = false;

  isAdmin: boolean;

  get isGestionnaire(): boolean {
    return !this.isConsultant;
  }

  get showConsultantCommentBanner(): boolean {
    return this.isGestionnaire && !!this.specificData?.commentaire?.trim() && !this.operation.commentaireConsultantLu;
  }

  demembrementOptions: OuiButtonToggleOption<string>[];
  modePaiementOptions: OuiSelectOption<string>[];
  transmissionOptions: OuiSelectOption<string>[];
  typeTauxFraisOptions: OuiButtonToggleOption<string>[];
  impactTauxFraisOptions: OuiButtonToggleOption<string>[];
  typeSignaturePartenaireOptions: OuiSelectOption<string>[];
  objetModificationOptions: OuiSelectOption<string>[];

  transactionPersonnelleOptions: OuiSelectOption<string>[];

  tagOptions: OuiSelectOption<number>[] = [];
  tagControl = new FormControl<number[]>([], { nonNullable: true });

  isCancelEnabled = false;
  isRejectEnabled = false;
  isSetInstancePartenaireEnabled = false;
  isRestorable = false;
  menuGestionnaire: MenuNode[] = [];

  mailToConsultant: string;
  mailToAssistant: string;
  isConsultantHabilitationWarning: boolean = false;
  consultantHabilitations: {
    icon: string;
    class: string;
    libelle: string;
    startDate?: Date;
    endDate?: Date;
    id: number;
    isOperationHabilitation: boolean;
  }[] = [];
  consultant: ConsultantInfo;
  assistants: ConsultantInfo[];
  emailInError: EnvoiEmail[] = [];

  operationDeclaration: OperationHistoryRecord | null;

  get operationStatus(): OuiTagStatus {
    return this.operationService.getOperationStatusColor(this.operation, this.isConsultant);
  }

  // Valeurs affichées dans les pair infos
  isInformationsEditable: boolean = false;
  private optionLabelPipe = new OptionLabelPipe();

  get fondsEvenementiels(): string {
    if (!(this.operation.fondEvenementiels?.length ?? 0 > 0)) return 'Non';
    return this.operation.fondEvenementiels.map(fe => fe.libelle).join(', ');
  }

  wrapTransactionPersonnelleStatut(tp: TransactionPersonnelleEnum | undefined): string {
    switch (tp) {
      case TransactionPersonnelleEnum.AutoSouscriptionAvecProches:
        return 'Auto-souscription et personne(s) avec un lien familial ou étroit';
      case TransactionPersonnelleEnum.AutoSouscriptionAvecSalaries:
        return 'Auto-souscription et un de mes salariés';
      case TransactionPersonnelleEnum.AutoSouscription:
        return 'Auto-souscription';
      case TransactionPersonnelleEnum.PourProches:
        return 'Pour un proche';
      case TransactionPersonnelleEnum.PourSalaries:
        return 'Pour un salarié';
      case TransactionPersonnelleEnum.Non:
      default:
        return 'Non';
    }
  }

  get modesPaiement(): string | undefined {
    return this.specificData.modePaiement
      ?.map((mode: string) => this.optionLabelPipe.transform(mode, this.modePaiementOptions))
      .join(', ');
  }

  get typeSignature(): string | undefined {
    return this.operation.typeSignaturePartenaire
      ? this.optionLabelPipe.transform(this.operation.typeSignaturePartenaire, this.typeSignaturePartenaireOptions)
      : undefined;
  }

  get objetsModification(): string | undefined {
    return this.specificData.objetModification
      ? this.optionLabelPipe.transform(this.specificData.objetModification, this.objetModificationOptions)
      : undefined;
  }

  get demembrement(): string | undefined {
    return this.specificData.demembrement
      ? this.optionLabelPipe.transform(this.specificData.demembrement, this.demembrementOptions)
      : undefined;
  }

  get isContrepartie(): string {
    if (!this.specificData.contrepartieIsKnown) {
      return 'Non';
    }
    return 'Oui';
  }

  get contrepartieName(): string | undefined {
    return this.specificData.contrepartieName;
  }
  get typeTauxFrais(): string | undefined {
    return this.specificData.typeTauxFrais
      ? this.optionLabelPipe.transform(this.specificData.typeTauxFrais, this.typeTauxFraisOptions)
      : undefined;
  }

  get impactDiminutionFrais(): string | undefined {
    return this.specificData.impactDiminutionFrais
      ? this.optionLabelPipe.transform(this.specificData.impactDiminutionFrais, this.impactTauxFraisOptions)
      : undefined;
  }
  get transmissionOriginaux(): string | undefined {
    return this.specificData.transmissionOriginaux
      ? this.optionLabelPipe.transform(this.specificData.transmissionOriginaux, this.transmissionOptions)
      : undefined;
  }
  get transactionPersonnelle(): string | undefined {
    return this.specificData.transactionPersonnelle
      ? this.optionLabelPipe.transform(this.specificData.transactionPersonnelle, this.transactionPersonnelleOptions)
      : undefined;
  }

  get versementsProgrammesDisplayValue(): string | undefined {
    if (!this.shouldDisplayScpiRendementSpecificFields()) {
      return this.specificData.versementsProgrammes;
    }

    const value = this.specificData.versementsProgrammes;
    if (value) {
      return value;
    }

    return this.isDraftOperation() ? 'Non' : 'Non défini';
  }

  get reinvestissementDividendesDisplayValue(): string | undefined {
    if (!this.shouldDisplayScpiRendementSpecificFields()) {
      return undefined;
    }

    const value = this.specificData.reinvestissementDividendes;
    if (value) {
      return value;
    }

    return this.isDraftOperation() ? 'Non' : 'Non défini';
  }

  private shouldDisplayScpiRendementSpecificFields(): boolean {
    const operationConfigId = this.operation?.operationConfigId ?? this.operation?.operationConfig?.id;
    if (operationConfigId !== OperationsPanelInfoComponent.SCPI_RENDEMENT_SOUSCRIPTION_CONFIG_ID) {
      return false;
    }

    const code = this.operation?.produit?.habilitationNavigation?.code?.toUpperCase();
    if (!code) {
      return true;
    }

    return OperationsPanelInfoComponent.ELIGIBLE_HABILITATIONS.includes(code);
  }

  private isDraftOperation(): boolean {
    return OperationsPanelInfoComponent.DRAFT_STATUT_IDS.includes(this.operation?.statutId ?? -1);
  }

  protected reopenHistory: boolean = false;

  protected documentViewerPortal: TemplatePortal;
  @ViewChild('documentViewerPortalContent') documentViewerPortalContent: TemplateRef<unknown>;

  protected viewerFile?: FileMetadata;
  tagChips: OuiSelectOption[] = [];
  staticTags: OperationTag[] = [];
  showClearTagsButton: boolean = false;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private optionService: OptionsService,
    private authService: AuthService,
    private ouiDialogService: OuiDialogService,
    private snackbarService: OuiSnackbarService,
    private operationService: OperationsService,
    private loggerService: ApplicationInsightsService,
    private datepipe: DatePipe,
    private documentViewerPanelService: BoxDocumentViewerService,
    private queryManager: QueryManagerService
  ) {}

  async markConsultantCommentAsRead(banner: OuiBanner): Promise<void> {
    if (this.isMarkingConsultantCommentAsRead) return;
    const operation = this.operation;
    this.isMarkingConsultantCommentAsRead = true;
    try {
      const result = await firstValueFrom(
        this.queryManager.mutate<{
          markConsultantCommentAsRead: Pick<Operation, 'id' | 'commentaireConsultantLu'> | null;
        }>({
          mutation: MARK_CONSULTANT_COMMENT_AS_READ,
          variables: { operationId: operation.id },
        })
      );
      if (result.error || result.errors?.length || !result.data?.markConsultantCommentAsRead?.commentaireConsultantLu) {
        throw new Error('Consultant comment read status was not saved');
      }
      operation.commentaireConsultantLu = true;
    } catch (error) {
      banner.open();
      this.loggerService.logException(error instanceof Error ? error : new Error(String(error)));
      this.snackbarService.open("La lecture du commentaire n'a pas pu être enregistrée. Veuillez réessayer.", 'error', 5000);
    } finally {
      this.isMarkingConsultantCommentAsRead = false;
    }
  }

  ngOnInit() {
    this.isAdmin = this.authService.isAdminRole();
    this.demembrementOptions = this.optionService.getDemembrementOptions();
    this.modePaiementOptions = this.optionService.getModePaiementOptions();
    this.transmissionOptions = this.optionService.getTransmissionOptions();
    this.typeTauxFraisOptions = this.optionService.getTypeTauxFraisOptions();
    this.impactTauxFraisOptions = this.optionService.getImpactTauxFraisOptions();
    this.typeSignaturePartenaireOptions = this.optionService.getTypeSignaturePartenaireOptions();
    this.objetModificationOptions = this.optionService.getObjetModificationOptions();
    this.transactionPersonnelleOptions = this.optionService.getTransactionPersonnelleOptions();

    if (this.isGestionnaire) {
      this.optionService.getOperationTagOptionsWithData(false).then(options => {
        this.tagOptions = options;
        setTimeout(() => {
          //FIXME setTimeout est nécessaire pour afficher les tags cochés dans le oui-select-filter
          this.tagControl.setValue(this.operation.tags.map(s => s.id));
          this.tagChips = this.tagOptions.filter(o => o.value && this.tagControl.value.includes(o.value));
          this.tagControl.valueChanges.pipe(distinctUntilChanged()).subscribe(value => {
            this.tagChips = this.tagOptions.filter(o => o.value && this.tagControl.value.includes(o.value));
            this.updateTag(value);
          });
        });
      });
    }

    this.setStaticTags();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['operation']) {
      this.onOperationChanges();
    }
  }

  private setStaticTags(): void {
    this.staticTags = this.operation.tags?.filter(t => t.isPermanent === true);
  }

  private readonly restorableTriggers = new Set<OperationStateTransitionTrigger>(
    Object.values(OperationStateTransitionTrigger).filter(
      (value): value is OperationStateTransitionTrigger => typeof value === 'string' && value.startsWith('RESTORE')
    )
  );

  private onOperationChanges() {
    this.isCancelEnabled = this.isTriggerIncluded(OperationStateTransitionTrigger.GestionnaireCancelOperation);
    this.isRejectEnabled = this.isTriggerIncluded(OperationStateTransitionTrigger.GestionnaireRefuseOperation);
    this.isSetInstancePartenaireEnabled = this.isTriggerIncluded(
      OperationStateTransitionTrigger.GestionnaireDeclareInstancePartenairePresence
    );

    // Vérifier si au moins un trigger est inclus et mettre à jour isRestorable
    this.isRestorable = Array.from(this.operation?.activeOperationStateTransitionTriggers || []).some(trigger =>
      this.restorableTriggers.has(trigger)
    );

    this.getEmailErrors();
    this.menuGestionnaire = [
      {
        name: "Indiquer la présence d'instances partenaires",
        disabled: !this.isSetInstancePartenaireEnabled,
        action: () => this.onSetInstancePartenaire(),
      },
      { name: "Annuler l'opération", disabled: !this.isCancelEnabled, action: () => this.onCancelOperation() },
      { name: "Refuser l'opération", disabled: !this.isCancelEnabled, action: () => this.onRejectOperation() },
      { name: 'Editer commentaire gestionnaire', action: () => this.onEditGestionnaireCommentClick.emit() },
      {
        name: 'Revenir au statut précédent',
        disabled: !this.isRestorable,
        action: () => {
          const trigger = this.getFirstAvailableRestoreTrigger();
          if (trigger) {
            this.onRestoreClick.emit(trigger);
          }
        },
      },
      { name: 'Envoyer un message', action: () => this.onNotifyConsultantEmailClick() },
      { name: 'Actualiser PPE/GDA', action: async () => await this.ActualizePpeGda() },
    ];

    if (this.operation) {
      this.parseSpecificData();
      this.setConsultantInfo(this.operation);
      if (this.tagOptions?.length > 0) {
        this.tagControl.setValue(this.operation.tags.map(t => t.id) ?? [], { emitEvent: false });
        this.tagChips = this.tagOptions.filter(o => o.value && this.tagControl.value.includes(o.value));
      }

      // Etats où l'édition des informations est autorisée
      this.isInformationsEditable = isOperationBackofficeEditable(this.operation.statutId);
    }
  }

  private async ActualizePpeGda() {
    try {
      const tags = await this.operationService.getOperationTags(true);
      const ppeTag = tags.find(t => t.code === 'PPE');
      const gdaTag = tags.find(t => t.code === 'GDA');

      // Validation stricte des tags requis
      if (!ppeTag || !gdaTag) {
        const missingTags = [!ppeTag && 'PPE', !gdaTag && 'GDA'].filter(Boolean).join(' et ');

        this.snackbarService.open(`Le(s) tag(s) ${missingTags} n'ont pas été trouvés.`, 'error', 5000);
        return;
      }

      const currentTagIds = this.operation.tags?.map(t => t.id) ?? [];
      const updatedTagIds = this.calculateUpdatedTagIds(currentTagIds, ppeTag, gdaTag);

      // Mise à jour seulement si nécessaire
      if (this.hasTagChanges(currentTagIds, updatedTagIds)) {
        await this.updateTag(updatedTagIds, true); // bypassMerge = true pour ActualizePpeGda
      }
    } catch (error) {
      this.handleActualizeError(error);
    }
  }

  private calculateUpdatedTagIds(currentTagIds: number[], ppeTag: OperationTag, gdaTag: OperationTag): number[] {
    let tagIds = [...currentTagIds];

    // Configuration des règles de tags
    const tagRules = [
      {
        tag: ppeTag,
        shouldHave: this.operation.investisseur.ppeBackOffice,
      },
      {
        tag: gdaTag,
        shouldHave: this.operation.investisseur.gdaBackOffice,
      },
    ];

    tagRules.forEach(({ tag, shouldHave }) => {
      const hasTag = tagIds.includes(tag.id);

      if (shouldHave && !hasTag) {
        tagIds.push(tag.id);
      } else if (!shouldHave && hasTag) {
        tagIds = tagIds.filter(id => id !== tag.id);
      }
    });

    return tagIds;
  }

  private hasTagChanges(original: number[], updated: number[]): boolean {
    return original.length !== updated.length || !original.every(id => updated.includes(id));
  }

  private handleActualizeError(error: any): void {
    const errorMessage = 'Erreur lors de la synchronisation des tags PPE/GDA';
    this.snackbarService.open(errorMessage, 'error', 5000);
    this.loggerService.logException(new Error(errorMessage, { cause: error }));
  }

  private getFirstAvailableRestoreTrigger(): OperationStateTransitionTrigger | undefined {
    return this.operation?.activeOperationStateTransitionTriggers.find(trigger => this.restorableTriggers.has(trigger));
  }

  private parseSpecificData() {
    try {
      this.specificData = JSON.parse(this.operation?.donneesSpecifiques);
    } catch (error: any) {
      this.specificData = {};
      const newError = new Error('Données Spécifiques : JSON Parser Error', error);
      this.loggerService.logException(newError);
    }
  }

  onBackArrowClick() {
    this.onBackArrowClicked.emit();
  }

  getEmailErrors() {
    if (this.operation && this.authService.isBackOffice()) {
      const errors = this.operation.emails.filter(
        email => email.statutEnvoi === EnvoiEmailStatut.Error && !email.isErrorAcknowledged
      );
      this.emailInError = errors ?? [];
    }
  }

  onHistoriqueClick() {
    const dialogRef = this.ouiDialogService.openDialog(
      OperationHistoriqueDialogComponent,
      { operationId: this.operation.id },
      '720px',
      '700px'
    );

    const dialogSub = dialogRef.componentInstance.documentOpen.subscribe((file: FileMetadata) => {
      this.reopenHistory = true;
      this.openDocumentViewer(file);

      dialogRef.close();
    });

    dialogRef.afterClosed().subscribe(() => {
      dialogSub.unsubscribe();
    });
  }

  isTriggerIncluded(trigger: OperationStateTransitionTrigger): boolean {
    return this.operation?.activeOperationStateTransitionTriggers.includes(trigger);
  }

  onRejectOperation() {
    this.onRejectClick.emit();
  }
  onCancelOperation() {
    this.onCancelClick.emit();
  }

  onSetInstancePartenaire() {
    this.onSetInstancePartenaireClick.emit();
  }

  onDeclareMailReception() {
    this.onMailReceptionClick.emit();
  }

  onNotifyConsultantEmailClick() {
    this.onSendConsultantEmailClick.emit();
  }

  onGestionnaireClick() {
    const modalRef = this.ouiDialogService.openDialog(
      OperationsDispatcherComponent,
      {
        operations: [this.operation],
        operationIds: [this.operation.id],
        isSingleDispatch: true,
      },
      'auto',
      '1280px'
    );

    modalRef.afterClosed().subscribe((afteclosedData: Operation[]) => {
      if (!afteclosedData || !afteclosedData[0]) return;
      const updatedOne = afteclosedData[0];
      if (updatedOne) {
        this.operation.statutId = updatedOne.statutId;
        this.operation.statut = updatedOne.statut;
        this.operation.gestionnaireId = updatedOne.gestionnaireId;
        this.operation.gestionnaire = updatedOne.gestionnaire;
        this.operation.activeOperationStateTransitionTriggers = updatedOne.activeOperationStateTransitionTriggers;
        this.operation.operationActionRights = updatedOne.operationActionRights;
        this.onGestionnaireAssigned.emit(this.operation);
        this.onOperationChanges();
      }
    });
  }

  setConsultantInfo(operation: Operation) {
    // A consultant can be several individuals and ConsulantPersonnePhysique, ConsultantEmail, ConsultantTelephone are arrays of objects
    // because the Finzzle model is what it is, we presume that index 0 is the main one, and the others are called assistant(e).
    const operationConsultant = operation.consultant;
    this.assistants = [];
    operationConsultant?.personnePhysique?.forEach((pp, index) => {
      const personneInfo: ConsultantInfo = {
        displayName: `${pp?.nom ?? ''} ${pp?.prenom ?? ''}`,
        email: this.tryGetConsultantEmail(operationConsultant, index),
        mailToLink: this.tryGetConsultantEmail(operationConsultant, index)
          ? `mailto:${this.tryGetConsultantEmail(operationConsultant, index)}`
          : null,
        phoneNumber: this.tryGetConsultantPhoneNumber(operationConsultant),
        commisionnement:
          operationConsultant.tauxRemuneration && !Number.isNaN(operationConsultant.tauxRemuneration)
            ? `${operationConsultant.tauxRemuneration * 100}%`
            : `non renseigné`,
        titre: operationConsultant.titreCodeNavigation?.libelle ?? 'non renseigné',
      };
      index === 0 ? (this.consultant = personneInfo) : this.assistants.push(personneInfo);
    });

    this.listAndCheckHabilitations(operation);
  }

  tryGetConsultantPhoneNumber(consultant: Consultant): string | null {
    //first check if there is a mobile phone, otherwise take the first phone
    const MOBILE_PHONE_CODE = 'MOB';
    if (consultant?.telephone) {
      const mobile = consultant.telephone.find(t => t.typeTelephoneCode === MOBILE_PHONE_CODE);
      if (mobile) {
        return `${mobile.numero}`;
      }
      const otherPhone = consultant.telephone[0];
      if (otherPhone) {
        return `${otherPhone.numero}`;
      }
    }
    return null;
  }

  tryGetConsultantEmail(consultant: Consultant, index: number): string | null {
    // because the Finzzle model is what it is, we have no garanty that length is the same for ConsulantPersonnePhysique, ConsultantEmail, ConsultantTelephone.
    try {
      if (consultant?.email && consultant?.email[index]) {
        return `${consultant?.email[index]?.adresse}`;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  listAndCheckHabilitations(operation: Operation) {
    const consultHabs: HabilitationCategories = filterHabilitations(
      operation.consultant?.consultantHabilitations ?? []
    );
    this.isConsultantHabilitationWarning = !checkConsultantHabilitation(
      operation?.produit?.habilitationNavigation?.code,
      consultHabs.valid.map(c => c.codeHabilitation!)
    );

    // compute habilitations
    this.consultantHabilitations = [];
    // push valid habilitations
    consultHabs.valid.forEach(element =>
      this.consultantHabilitations.push({
        icon: 'verified_user',
        class: 'color-valid',
        libelle: element.habilitation?.libelle ?? element.codeHabilitation ?? 'habilitation inconnue',
        startDate: element.dateDebut,
        endDate: element.dateFin,
        id: element.habilitationId,
        isOperationHabilitation: operation.produit?.habilitation == element.habilitationId,
      })
    );
    //push expired habilitations
    consultHabs.expired.forEach(element =>
      this.consultantHabilitations.push({
        icon: 'gpp_bad',
        class: 'color-error',
        libelle: element.habilitation?.libelle ?? element.codeHabilitation ?? 'habilitation inconnue',
        startDate: element.dateDebut,
        endDate: element.dateFin,
        id: element.habilitationId,
        isOperationHabilitation: operation.produit?.habilitation == element.habilitationId,
      })
    );
    // check if we have already push the operation habilitation, if not we add it
    if (this.consultantHabilitations.findIndex(h => h.isOperationHabilitation) === -1) {
      this.consultantHabilitations.push({
        icon: 'gpp_bad',
        class: 'color-error',
        libelle:
          operation.produit?.habilitationNavigation?.libelle ??
          operation.produit?.habilitationNavigation?.code ??
          'habilitation inconnue',
        startDate: undefined,
        endDate: undefined,
        id: operation.produit?.habilitation,
        isOperationHabilitation: true,
      });
    }
    // Sort the habilitations to put the operation habilitation at the top (first place)
    this.consultantHabilitations.sort((a, b) =>
      a.isOperationHabilitation === b.isOperationHabilitation ? 0 : a.isOperationHabilitation ? -1 : 1
    );
  }

  openFicheLiaisonDialog() {
    this.ouiDialogService.openDialog(FicheLiaisonDialogComponent, { operation: this.operation }, 'auto', '560px');
  }

  onSendPartenaireEmailClick() {
    const modalRef = this.ouiDialogService.openDialog(NotifyMailPartenaireComponent, {
      operationId: this.operation.id,
    });
    modalRef.afterClosed().subscribe((mailId: number) => {
      if (mailId) {
        this.emailInError = [];
      }
    });
  }

  editNotImplemented() {
    this.snackbarService.open("La modification du champ n'est pas implémentée.", 'error');
  }

  isCommandEditable(): boolean {
    return this.isGestionnaire && this.operation.operationType === OperationType.Souscription;
  }

  editAramisCommande() {
    const dialogRef = this.ouiDialogService.openDialog(
      CommandeAramisDialogComponent,
      {
        operation: this.operation,
      },
      'auto',
      '630px'
    );

    dialogRef.afterClosed().subscribe((value: Operation | undefined) => {
      this.operationChange.emit();
    });
  }

  openEditDialog(champ: OperationChamp, currentValue: any) {
    const dialogRef = this.ouiDialogService.openDialog(
      OperationUpdateDialogComponent,
      <OperationUpdateDialogData>{
        operation: this.operation,
        champ: champ,
        currentValue: currentValue,
      },
      'auto',
      '580px'
    );

    dialogRef.afterClosed().subscribe((value: Operation | undefined) => {
      if (!value) return;
      this.operationChange.emit();
    });
  }

  private async updateTag(tagIds: number[], bypassMerge: boolean = false) {
    try {
      var finalTagIds = bypassMerge ? tagIds : this.mergeTags(this.operation.tags, tagIds);
      var result = await this.operationService.updateOperationTag(this.operation.id, finalTagIds);

      if (result) {
        this.operation.tags = result.tags;
        this.setStaticTags();
      }
    } catch (error: any) {
      var errorMessage = 'Une erreur est survenue durant la mise à jour du tag';
      this.snackbarService.open(errorMessage, 'error', 5000);
      this.loggerService.logException(new Error(errorMessage, { cause: error }));
      this.tagControl.setValue(this.operation.tags.map(t => t.id) ?? [], { emitEvent: false });
    }
  }

  // Fusionne les tags permanents existants avec les nouveaux tags en évitant les doublons
  private mergeTags(existingTags: OperationTag[], newTags: number[]): number[] {
    const existingPermanentTags = existingTags.filter(tag => tag.isPermanent);
    const existingPermanentTagIds = new Set(existingPermanentTags.map(tag => tag.id));

    // Filtrer les nouveaux tags (exclure les permanents déjà présents)
    const newTagIds = newTags.filter(tagId => !existingPermanentTagIds.has(tagId));

    return [...existingPermanentTagIds, ...newTagIds];
  }

  protected formatDate(date?: Date): string {
    if (!date) {
      return '--/--/--';
    }
    return this.datepipe.transform(date, 'dd/MM/yyyy') ?? '--/--/--';
  }

  openDocumentViewer(fileMetadata?: FileMetadata) {
    this.documentViewerPanelService.openSimpleDocumentViewer({ fileMetadata: fileMetadata });

    if (this.reopenHistory) {
      firstValueFrom(this.documentViewerPanelService.viewerClosed$).then(() => {
        this.onHistoriqueClick();
        this.reopenHistory = false;
      });
    }
  }

  closeFileViewer() {
    this.documentViewerPanelService.closeDocumentViewer();
  }

  onClearTags() {
    var permanentTagIds = this.operation.tags?.filter(t => t.isPermanent).map(t => t.id);
    this.tagControl.setValue(permanentTagIds); // On garde les tags permanents
  }

  onRemoveTag(tagId: number) {
    if (
      this.operation.tags
        ?.filter(t => t.isPermanent === true)
        .map(t => t.id)
        .includes(tagId)
    ) {
      return; // ne rien faire si le tag est permanent
    }

    const tagIdsList = this.tagControl.value.filter(t => t !== tagId);
    this.tagControl.setValue(tagIdsList);
  }

  getAppearance(tag: { code: string; isPermanent: boolean }) {
    return getTagAppearance(tag);
  }
}
